import React, { useState, useEffect, useRef } from 'react';
import { Row, Col } from "reactstrap";
import IaTrend from "../components/IaTrend";

const CARGA_LABELS = ['Nivel 1', 'Nivel 2', 'Nivel 3'];

const VOLATILITY_MAP = {
  low: { label: 'Baja', color: '#2dce89', bg: 'rgba(45,206,137,0.12)' },
  intermediate: { label: 'Media', color: '#fb6340', bg: 'rgba(251,99,64,0.12)' },
  high: { label: 'Alta', color: '#f5365c', bg: 'rgba(245,54,92,0.12)' },
};

const style = {
  colHeader: {
    fontSize: 12,
    fontWeight: 700,
    color: '#525f7f',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    textAlign: 'center',
    padding: '8px 4px',
    borderBottom: '2px solid #e9ecef',
  },
  cell: {
    padding: '10px 4px',
    borderBottom: '1px solid #f0f0f0',
    textAlign: 'center',
    verticalAlign: 'middle',
    fontSize: '13px',
  },
  priceValue: {
    fontSize: 14,
    fontWeight: 600,
    color: '#32325d',
  },
};

const ProbBar = ({ value }) => {
  const num = parseFloat(value) || 0;
  return (
    <div style={{ width: '100%', marginTop: 4 }}>
      <div style={{ height: 4, background: '#e9ecef', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(num, 100)}%`, background: '#11cdef', borderRadius: 2 }} />
      </div>
    </div>
  );
};

const Matriz = (props) => {
  const [criptoWs, setCriptoWs] = useState(() => {
    if (props.symbol && props.symbol.toLowerCase().includes('btc')) return 'btcusdt';
    if (props.symbol && props.symbol.toLowerCase().includes('eth')) return 'ethusdt';
    if (props.symbol && props.symbol.toLowerCase().includes('bnb')) return 'bnbusdt';
    if (props.symbol && props.symbol.toLowerCase().includes('sol')) return 'solusdt';
    if (props.symbol && props.symbol.toLowerCase().includes('sui')) return 'suiusdt';
    if (props.symbol && props.symbol.toLowerCase().includes('doge')) return 'dogeusdt';
    return 'btcusdt';
  });
  const [priceData, setPriceData] = useState(null);
  const [priceDir, setPriceDir] = useState(null); // 'up' | 'down' | null
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const prevPrice = useRef(null);
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);

  useEffect(() => {
    if (props.symbol && props.symbol.toLowerCase().includes('btc')) setCriptoWs('btcusdt');
    else if (props.symbol && props.symbol.toLowerCase().includes('eth')) setCriptoWs('ethusdt');
    else if (props.symbol && props.symbol.toLowerCase().includes('sol')) setCriptoWs('solusdt');
    else if (props.symbol && props.symbol.toLowerCase().includes('bnb')) setCriptoWs('bnbusdt');
    else if (props.symbol && props.symbol.toLowerCase().includes('sui')) setCriptoWs('suiusdt');
    else if (props.symbol && props.symbol.toLowerCase().includes('doge')) setCriptoWs('dogeusdt');
  }, [props.symbol]);

  const connectWebSocket = () => {
    try {
      const wsUrl = `wss://stream.binance.com:9443/ws/${criptoWs.toLowerCase()}@ticker`;
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        setIsConnected(true);
        setError(null);
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const newPrice = parseFloat(data.c);
          if (prevPrice.current !== null) {
            setPriceDir(newPrice > prevPrice.current ? 'up' : newPrice < prevPrice.current ? 'down' : null);
          }
          prevPrice.current = newPrice;
          setPriceData(data.c);
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.current.onerror = () => {
        setError('Error de conexión WebSocket');
      };

      ws.current.onclose = () => {
        setIsConnected(false);
        if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = setTimeout(() => {
          if (ws.current?.readyState !== WebSocket.OPEN) connectWebSocket();
        }, 3000);
      };
    } catch (err) {
      setError('Error creando conexión WebSocket');
    }
  };

  const disconnectWebSocket = () => {
    if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
    if (ws.current) { ws.current.close(); ws.current = null; }
  };

  useEffect(() => {
    connectWebSocket();
    return () => disconnectWebSocket();
  }, [criptoWs]);

  const getValue = ({ key, carg }, base = false) => {
    if (!props.matriz) return "—";
    try {
      if (!base) {
        const vol = props.matriz.indexVolatility;
        const level = vol <= 5.99 ? "low" : vol <= 9.99 ? "intermediate" : "high";
        const value = props.matriz[level]?.data?.[carg]?.[key];
        if (value === undefined || value === null) return "—";
        if (typeof value === "number") {
          return value.toFixed(2);
        }
        return String(value);
      }
      const value = props.matriz[key];
      if (value === undefined || value === null) return "—";
      if (typeof value === "number") {
        return value.toFixed(2);
      }
      return String(value);
    } catch (e) {
      console.error("Error en getValue:", e);
      return "—";
    }
  };

  const getVolatilityLevel = () => {
    if (!props.matriz) return null;
    const vol = props.matriz.indexVolatility;
    if (vol <= 5.99) return 'low';
    if (vol <= 9.99) return 'intermediate';
    return 'high';
  };

  const symbolLabel = props.symbol ? props.symbol.replace('USDT', '').replace('usdt', '') : '';
  const priceDecimals = priceData && parseFloat(priceData) < 10 ? 4 : 2;
  const priceFormatted = priceData ? parseFloat(priceData).toLocaleString('en-US', { minimumFractionDigits: priceDecimals, maximumFractionDigits: priceDecimals }) : '—';
  const volLevel = getVolatilityLevel();
  const volInfo = volLevel ? VOLATILITY_MAP[volLevel] : null;


  return (
    <div style={{ padding: '0 8px' }}>

      {/* Price bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '14px 20px',
        background: 'white',
        borderRadius: 12,
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        marginBottom: 16,
      }}>
        {/* WS status dot */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: isConnected ? '#2dce89' : '#fb6340',
            display: 'inline-block',
          }} title={isConnected ? 'WebSocket conectado' : 'Reconectando…'} />
          <span style={{ fontSize: 11, color: '#8898aa' }}>{isConnected ? 'En vivo' : 'Reconectando…'}</span>
        </div>

        <div style={{ width: 1, height: 28, background: '#e9ecef' }} />

        {/* Symbol + price */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#8898aa' }}>{symbolLabel}/USDT</span>
          <span style={{ fontSize: 26, fontWeight: 800, color: '#32325d', letterSpacing: '-0.5px' }}>
            ${priceFormatted}
          </span>
          {priceDir === 'up' && <span style={{ fontSize: 18, color: '#2dce89' }}>▲</span>}
          {priceDir === 'down' && <span style={{ fontSize: 18, color: '#f5365c' }}>▼</span>}
        </div>

        {/* Volatility badge */}
        {volInfo && (
          <>
            <div style={{ width: 1, height: 28, background: '#e9ecef', marginLeft: 'auto' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#8898aa', fontWeight: 600 }}>VOLATILIDAD</span>
              <span style={{
                background: volInfo.bg,
                color: volInfo.color,
                fontWeight: 700,
                fontSize: 13,
                borderRadius: 6,
                padding: '3px 10px',
                border: `1px solid ${volInfo.color}33`,
              }}>
                {volInfo.label}
              </span>
              <span style={{ fontSize: 13, color: '#8898aa' }}>{getValue({ key: 'indexVolatility' }, true)}</span>
            </div>
          </>
        )}
      </div>

      {/* Main grid */}
      <Row style={{ marginLeft: 0, marginRight: 0, gap: 12 }}>

                        {/* Trading matrix */}
        <Col style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '0px', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0px', backgroundColor: '#f9f9fb', borderBottom: '2px solid #e9ecef' }}>
            <div style={{ padding: '12px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#525f7f', textTransform: 'uppercase' }}>Nivel</div>
            <div style={{ padding: '12px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#f5365c', textTransform: 'uppercase' }}>Compra</div>
            <div style={{ padding: '12px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#11cdef', textTransform: 'uppercase' }}>Probabilidad</div>
            <div style={{ padding: '12px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#2dce89', textTransform: 'uppercase' }}>Venta</div>
          </div>

                                        {/* Body rows */}
          {[0, 1, 2].map((rowIndex) => {
            if (!props.matriz) return null;
            
            const vol = props.matriz.indexVolatility;
            const level = vol <= 5.99 ? 'low' : vol <= 9.99 ? 'intermediate' : 'high';
            const rowData = props.matriz[level]?.data?.[rowIndex];
            
            if (!rowData) return null;
            
            const probability = rowData.probability;
            const support = rowData.support;
            const resistence = rowData.resistence;
            const levelLabel = CARGA_LABELS[rowIndex] || `Nivel ${rowIndex + 1}`;
            
            return (
              <div key={`row-${rowIndex}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0px', backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f9f9fb', borderBottom: '1px solid #e9ecef' }}>
                <div style={{ padding: '14px', textAlign: 'center', fontWeight: 700, color: '#525f7f', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {levelLabel}
                </div>
                <div style={{ padding: '14px', textAlign: 'center', borderLeft: '3px solid #f5365c22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#f5365c' }}>
                    ${support ? support.toFixed(2) : '—'}
                  </span>
                </div>
                <div style={{ padding: '14px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#32325d', marginBottom: 6 }}>{probability}%</div>
                  <div style={{ width: '80%' }}>
                    <div style={{ height: 5, background: '#e9ecef', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(probability || 0, 100)}%`, background: '#11cdef', borderRadius: 2 }} />
                    </div>
                  </div>
                </div>
                <div style={{ padding: '14px', textAlign: 'center', borderLeft: '3px solid #2dce8922', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#2dce89' }}>
                    ${resistence ? resistence.toFixed(2) : '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </Col>

        {/* Right column: ideal prices + IA */}
        <Col xs={3} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 0 }}>

          {/* Stop-loss: sellIdeal es el nivel de salida de emergencia, no un objetivo de compra */}
          <div style={{
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            padding: '14px 16px',
            borderLeft: '4px solid #f5365c',
          }}>
            <div style={{ fontSize: 11, color: '#8898aa', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Stop-loss</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#f5365c' }}>${getValue({ key: 'sellIdeal' }, true)}</div>
          </div>

          {/* Ideal sell */}
          <div style={{
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            padding: '14px 16px',
            borderLeft: '4px solid #2dce89',
          }}>
            <div style={{ fontSize: 11, color: '#8898aa', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Venta ideal</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#2dce89' }}>${getValue({ key: 'saleIdeal' }, true)}</div>
          </div>

          <IaTrend prediction={props.statusBot?.prediction} />
        </Col>
      </Row>

      {error && (
        <div style={{ marginTop: 12, padding: '8px 16px', background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8, color: '#c53030', fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default Matriz;
